import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum'
import '@src/globals.css'
import '@src/SectionRenderer/sections/gridSlider/gridSlider.scss'
import '@src/quince-components/src/atoms/swiper/swiper.scss'
import '@src/quince-site-integrated/src/components/FormElements/inputStyles.scss'
import '@src/quince-site-integrated/src/components/FormElements/passwordStyles.scss'
import '@src/pages/contact-us/contact-us-components/glady-help-center/gladly-help-center-external.scss'
import '@src/pages/contact-us/contact-us-components/glady-help-center/gladly-help-center-external-ds.scss'
import '@src/SectionRenderer/sections/heroBanner/b2bHero.scss'
import '@src/quince-styles/scss/index.scss' // THIS IS INTENTIONAL DO NOT CHANGE THE ORDER!!!!! THIS SHOULD ALWAYS BE THE LAST IMPORT
import { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { appWithTranslation, useTranslation } from 'next-i18next'
import { useEffect, useState, useRef } from 'react'
import { isMobile } from 'react-device-detect'

import { QueryClient, QueryClientProvider, HydrationBoundary, type DehydratedState } from '@lastbrand/sdk-http'
import { DD_RUM_APP_ID, DD_RUM_ENABLED_FLAG, DD_RUM_CLIENT_TOKEN, DD_RUM_SERVICE } from '@src/config/env'
import { LocaleConfigContext } from '@src/experiments/statsig-integration/locale-config.context'
import { useScrollSnapshotUnderLock } from '@src/hooks/use-scroll-snapshot-under-lock'
import { useIsomorphicLayoutEffect } from '@src/hooks/useIsomorphicLayoutEffect'
import { resolveAndConfigureAuth } from '@src/modules/auth/resolve-auth-config'
import { initializeClientEntry } from '@src/modules/client-entry'
import {
  hydrateUserAgnosticGlobalFeatureGatesFromNextData,
  setUserAgnosticGlobalFeatureGatesInMemory,
} from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { CookieBanner } from '@src/quince-components/src'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import { useOnRouteUpdateWithStateFlush } from '@src/quince-site-common/src/hooks/useOnRouteUpdateWithStateFlush'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { useReferralCouponTelemetry } from '@src/quince-site-integrated/src/hooks/useReferralCouponTelemetry'
import { useReferralUrlLandingTelemetry } from '@src/quince-site-integrated/src/hooks/useReferralUrlLandingTelemetry'
import { getCookie } from '@src/quince-site-integrated/src/utils'
import { ServerSideBaseProps } from '@src/server-side/api/api.types'
import { datadogRumBeforeSend } from '@src/telemetry/datadog-rum-before-send'
import { setPartytownEnabledInMemory } from '@src/telemetry/partytown/partytown-config.browser'
import { setRegionRumContext } from '@src/telemetry/region-rum.helper'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { initializeFeCommon } from '@src/utils/feCommonInitialize'
import { initMSW } from '@src/utils/mockServiceWorkerInitalize'
import {
  setDesignSystemEnabledInMemory,
  setDSRampUserInMemory,
  syncDesignSystemHtmlAttributes,
} from '@src/utils/should-use-design-system'
import {
  setSvdCardEnabledInMemory,
  setSvdFlowEnabledInMemory,
  syncSvdHtmlAttributes,
} from '@src/utils/should-use-svd-card'

import nextI18nextConfig from '../../next-i18next.config'

// Code-split the Forter loader (+ its ~40KB vendor snippet) into its own chunk, mounted (below) only when
// the enable_forter_fraud_check kill-switch is on — so nothing Forter-related is downloaded/parsed when it
// is off. ssr:false: the snippet runs browser-only (device profiling).
const ForterScript = dynamic(() => import(/* webpackChunkName: "forter" */ '@src/modules/forter/forter-script'), {
  ssr: false,
})

if (DD_RUM_ENABLED_FLAG && DD_RUM_APP_ID && DD_RUM_CLIENT_TOKEN) {
  const datadogConfig: RumInitConfiguration = {
    beforeSend: datadogRumBeforeSend,
    applicationId: DD_RUM_APP_ID,
    clientToken: DD_RUM_CLIENT_TOKEN,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: DD_RUM_SERVICE,
    env: process.env.NEXT_PUBLIC_DD_RUM_ENV,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    sessionSampleRate: 10,
    // All of those 1% sessions will include session replay
    sessionReplaySampleRate: 1,
    traceSampleRate: 10,
    telemetrySampleRate: 1,
    // @i18n-ignore
    defaultPrivacyLevel: 'mask-user-input',
    // Enable tracking of user interactions, resources, and long tasks (required for Clickmaps)
    trackUserInteractions: true,
    // Source action names from our e2e testids so the SDK skips its DOM-walking name
    // synthesis on every interaction (INP tax). `data-dd-action-name` still wins where set —
    // used as a curated override where a testid is missing or dynamic (e.g. Contentful ids).
    // eslint-disable-next-line i18next/no-literal-string
    actionNameAttribute: 'data-testid',
    trackResources: true,
    trackLongTasks: true,
    // TODO: Uncomment this when we have a way to trigger session-replay programmatically, Now Session Replay starts recording automatically when calling init()
    // startSessionReplayRecordingManually: true, // Allow to do session-replay programmatically if needed
    allowedTracingUrls: [
      // @i18n-ignore
      { match: 'https://api-prod-public.onequince.com/', propagatorTypes: ['tracecontext'] },
      // @i18n-ignore
      { match: 'https://api-qa.onequince.com/', propagatorTypes: ['tracecontext'] },
    ],
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  datadogRum.init(datadogConfig)
  // console.log('Datadog initialized with configuration', datadogConfig)
}

if (isBrowser()) {
  hydrateUserAgnosticGlobalFeatureGatesFromNextData()
  const onAuthError = (error: Error, context: Record<string, unknown>) => {
    captureException(error, {
      module: MODULES.UNIVERSAL_AUTH,
      feature: FEATURES.UNIVERSAL_AUTH,
      extraInfo: context,
    })
  }
  resolveAndConfigureAuth(onAuthError)
}

initializeFeCommon()

// Partial: `_error`/404 and pages that skip `getServerSideBaseProps` (e.g. find-my-order)
// render _app with none of the base props, so every field may be absent at runtime.
type AppPageProps = Partial<ServerSideBaseProps> & {
  dehydratedState?: DehydratedState
  isB2BLoginPage?: boolean
}

function MyApp({ Component, pageProps, router }: AppProps<AppPageProps>) {
  const { t } = useTranslation('common')
  // const legacyUniversalAuthConfiguredRef = useRef(false)
  const queryClientRef = useRef<QueryClient | null>(null)
  if (queryClientRef.current === null) {
    queryClientRef.current = new QueryClient({
      defaultOptions: {
        queries: { refetchOnWindowFocus: false },
      },
    })
  }
  const queryClient = queryClientRef.current
  const dehydratedState: DehydratedState | undefined = pageProps.dehydratedState
  const [mswReady, setMswReady] = useState(process.env.NEXT_PUBLIC_ENABLE_MSW !== 'true')
  const isNativeView = getIsNativeViewClient()
  // Route prefix covers all /b2b/* on the main domain; isB2BLoginPage covers the
  // /log-in route when served from the B2B self-serve domain (business.quince.com).
  const isB2BMode = router.pathname.startsWith('/b2b') || Boolean(pageProps.isB2BLoginPage)
  const isForterFraudCheckEnabled = pageProps.userAgnosticGlobalFeatureGates?.enable_forter_fraud_check ?? false
  useOnRouteUpdateWithStateFlush([pdpStateManager], pageProps)
  useScrollSnapshotUnderLock()
  // Referral funnel step 1: fire once, wherever a referred shopper lands, if the URL carries a
  // recognized Yotpo referral param — mounted globally, not just on the referrer-facing page.
  // eslint-disable-next-line i18next/no-literal-string
  useReferralUrlLandingTelemetry('storefront')
  // Referral funnel step 3: listen globally for Yotpo's referred-customer discount event so the
  // coupon-generated count fires wherever the referred shopper is, not only on the rewards pages.
  useReferralCouponTelemetry()
  setUserAgnosticGlobalFeatureGatesInMemory(pageProps.userAgnosticGlobalFeatureGates)
  // The DS and SVD-card decisions are per-PAGE, so pages that ship no decision (find-my-order,
  // `_error`) pin fail-closed `false`: their markup renders the legacy fork, and the
  // commit-time sync below clears the scope attributes to match. A persisted panda scope
  // breaks legacy markup — the legacy fluid root font-size (normalize.scss) is scoped to
  // `:not([data-styles='panda'])`, so legacy rem sizing inflates ~1.4x under a leftover
  // panda scope (verified on pre-integ). `data-ds-ramp-user` / `data-svd-flow` are per-USER
  // facts, independent of the page — `undefined` passes through so the document-stamped
  // attribute survives pages that omit the prop.
  setDesignSystemEnabledInMemory(pageProps.isDesignSystemEnabled ?? false)
  setSvdCardEnabledInMemory(pageProps.isSvdEnabled ?? false)
  setDSRampUserInMemory(pageProps.isDSRampUser)
  setSvdFlowEnabledInMemory(pageProps.isSvdFlowEnabled)

  // Flip the <html> style-scope attributes at commit time (post-commit, pre-paint), never
  // during render: on a client-side navigation where the DS decision changes, a render-phase
  // mutation restyles the still-visible outgoing page for every frame until the incoming tree
  // commits (the is_ds toggle styling flash). The layout effect lands the attribute and the
  // new markup in the same paint. Deps mirror the setter inputs above — a new setter driven
  // by anything else must be added here too, or its attribute won't re-sync.
  useIsomorphicLayoutEffect(() => {
    syncDesignSystemHtmlAttributes()
    syncSvdHtmlAttributes()
  }, [pageProps.isDesignSystemEnabled, pageProps.isDSRampUser, pageProps.isSvdEnabled, pageProps.isSvdFlowEnabled])

  // Seed the Partytown experiment treatment membership so FbPixel's client init reads the same
  // per-user decision _document used at SSR. Same reset/guard rationale as the DS-ramp line above.
  if (!isBrowser() || pageProps.isPartytownEnabled !== undefined) {
    setPartytownEnabledInMemory(pageProps.isPartytownEnabled)
  }

  // Initialize MSW before rendering
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_MSW === 'true') {
      initMSW()
        .then(() => {
          setMswReady(true)
        })
        .catch(() => {
          setMswReady(true)
        })
    }
  }, [])

  // useEffect(() => {
  //   if (pageProps.userAgnosticGlobalFeatureGates.multi_pool_auth_enabled) {
  //     const regionId = getRegionIdFromLocale(pageProps.locale, pageProps.regionDetails)
  //     void authConfigDataHolder.initialize(regionId)
  //   }
  // }, [pageProps.userAgnosticGlobalFeatureGates, pageProps.locale, pageProps.regionDetails])

  // Stamp region onto RUM global context so events are queryable by market
  // (`@context.region` = US/CA/GB) instead of parsing it out of `@view.url_path`.
  useEffect(() => {
    setRegionRumContext(pageProps.locale, pageProps.regionDetails)
  }, [pageProps.locale, pageProps.regionDetails])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    initializeClientEntry(pageProps.userAgnosticGlobalFeatureGates)
    const isNewUserInner = getCookie('new-user-created') === 'random' && getCookie('_lb_user_first_access') === 'true'

    if (isNewUserInner) {
      trackEvent(TELEMETRY_EVENT_NAMES.NEW_USER_CREATED_RANDOM, {})
    }
  }, [pageProps.userAgnosticGlobalFeatureGates])

  if (!mswReady) {
    return null
  }

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        <Head>
          <meta
            name="viewport"
            // @i18n-ignore
            content={`width=device-width, initial-scale=1${getIsNativeViewClient() || isMobile ? ', maximum-scale=1' : ''}`}
          />
        </Head>
        <nav aria-label={t('accessibility_link')}>
          <a className="sr-only" href="/accessibility">
            {t('view_our_accessibility_statement')}
          </a>
        </nav>
        <LocaleConfigContext.Provider value={pageProps.localeConfig}>
          <Component {...pageProps} />
          {isForterFraudCheckEnabled ? <ForterScript /> : null}
          {!isNativeView && !isB2BMode ? <CookieBanner /> : null}
        </LocaleConfigContext.Provider>
      </HydrationBoundary>
    </QueryClientProvider>
  )
}

export default appWithTranslation(MyApp, nextI18nextConfig)
